import React, { useState, useContext, useEffect, useRef } from "react";
import { Link as RouterLink } from "react-router-dom";
import Button from "@material-ui/core/Button";
import CssBaseline from "@material-ui/core/CssBaseline";
import TextField from "@material-ui/core/TextField";
import Link from "@material-ui/core/Link";
import Grid from "@material-ui/core/Grid";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import Container from "@material-ui/core/Container";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import ColorModeContext from "../../layout/themeContext";
import useSettings from "../../hooks/useSettings";
import IconButton from "@material-ui/core/IconButton";
import Brightness4Icon from "@material-ui/icons/Brightness4";
import Brightness7Icon from "@material-ui/icons/Brightness7";
import Visibility from "@material-ui/icons/Visibility";
import VisibilityOff from "@material-ui/icons/VisibilityOff";
import InputAdornment from "@material-ui/core/InputAdornment";
import { Helmet } from "react-helmet";

import clsx from "clsx";
import { getBackendUrl } from "../../config";

const languageOptions = [
  { value: "pt-BR", label: "Português", emoji: "🇧🇷" },
  { value: "en", label: "English", emoji: "🇺🇸" },
  { value: "es", label: "Español", emoji: "🇪🇸" }
];

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100vw",
    height: "100vh",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    padding: "0",
    margin: "0",
    boxSizing: "border-box",
    overflow: "hidden",
    backgroundColor: theme.palette.type === "light" ? "#f5f5f5" : "#121212",
    background: theme.palette.type === "light"
      ? "linear-gradient(135deg, #1e3a8a 0%, #3b82f6 50%, #60a5fa 100%)"
      : "linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #334155 100%)",
    position: "relative",
    transition: "all 0.3s ease",

    // Padrão de pontos no fundo
    "&::before": {
      content: '""',
      position: "absolute",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      backgroundImage: theme.palette.type === "light"
        ? `radial-gradient(circle at 25% 25%, rgba(255,255,255,0.05) 1px, transparent 1px),
           radial-gradient(circle at 75% 75%, rgba(255,255,255,0.05) 1px, transparent 1px)`
        : `radial-gradient(circle at 25% 25%, rgba(255,255,255,0.02) 1px, transparent 1px),
           radial-gradient(circle at 75% 75%, rgba(255,255,255,0.02) 1px, transparent 1px)`,
      backgroundSize: "50px 50px",
      animation: "$float 20s ease-in-out infinite",
    },
  },

  "@keyframes float": {
    "0%, 100%": { transform: "translateY(0px) rotate(0deg)" },
    "50%": { transform: "translateY(-10px) rotate(180deg)" },
  },

  // Container centralizado perfeitamente
  containerLogin: {
    padding: "16px",
    maxWidth: "444px",
    width: "100%",
    position: "relative",
    zIndex: 10,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },

  paper: {
    backgroundColor: theme.palette.type === "light"
      ? "rgba(255, 255, 255, 0.95)"
      : "rgba(18, 18, 18, 0.95)",
    backdropFilter: "blur(20px)",
    WebkitBackdropFilter: "blur(20px)",
    boxShadow: theme.palette.type === "light"
      ? `0 20px 40px rgba(0, 0, 0, 0.1),
         0 1px 0 rgba(255, 255, 255, 0.2) inset,
         0 0 0 1px rgba(255, 255, 255, 0.1)`
      : `0 20px 40px rgba(0, 0, 0, 0.3),
         0 1px 0 rgba(255, 255, 255, 0.05) inset,
         0 0 0 1px rgba(255, 255, 255, 0.05)`,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: "40px 30px",
    borderRadius: "20px",
    maxWidth: "420px",
    width: "100%",
    margin: "0 auto",
    border: theme.palette.type === "light"
      ? "1px solid rgba(255, 255, 255, 0.2)"
      : "1px solid rgba(255, 255, 255, 0.1)",
    animation: "$slideInRight 0.8s cubic-bezier(0.4, 0, 0.2, 1)",
    transition: "all 0.3s ease",

    [theme.breakpoints.down("sm")]: {
      animation: "$slideInUp 0.8s cubic-bezier(0.4, 0, 0.2, 1)",
      borderRadius: "12px",
      padding: "35px 25px",
    },
  },

  "@keyframes slideInRight": {
    from: {
      opacity: 0,
      transform: "translateX(50px)",
    },
    to: {
      opacity: 1,
      transform: "translateX(0)",
    },
  },

  "@keyframes slideInUp": {
    from: {
      opacity: 0,
      transform: "translateY(30px)",
    },
    to: {
      opacity: 1,
      transform: "translateY(0)",
    },
  },

  avatar: {
    margin: theme.spacing(1),
    backgroundColor: "#3b82f6",
  },

  form: {
    width: "100%",
    marginTop: theme.spacing(1),
  },

  submit: {
    margin: theme.spacing(3, 0, 2),
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    borderRadius: "12px",
    padding: "12px 0",
    fontSize: "16px",
    fontWeight: 600,
    textTransform: "none",
    boxShadow: `0 4px 12px ${theme.palette.primary.main}40`,
    border: "none",
    transition: "all 0.3s ease",
    "&:hover": {
      backgroundColor: theme.palette.primary.dark,
      transform: "translateY(-2px)",
      boxShadow: `0 6px 20px ${theme.palette.primary.main}60`,
    },
    "&:active": {
      transform: "translateY(0)",
    },
  },

  powered: {
    color: "white",
  },

  // Logo - mantendo o sistema original
  logoImg: {
    width: "100%",
    maxWidth: "280px",
    height: "auto",
    maxHeight: "80px",
    margin: "0 auto 20px auto",
    filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.1))",
    // IMPORTANTE: Mantendo o sistema original de carregamento do logo
    content: "url(" + (theme.mode === "light" 
      ? theme.calculatedLogoLight() 
      : theme.calculatedLogoDark()) + ")",
  },

  iconButton: {
    position: "absolute",
    top: 15,
    right: 15,
    background: theme.palette.type === "light"
      ? "rgba(255, 255, 255, 0.1)"
      : "rgba(255, 255, 255, 0.05)",
    backdropFilter: "blur(10px)",
    border: theme.palette.type === "light"
      ? "1px solid rgba(255, 255, 255, 0.2)"
      : "1px solid rgba(255, 255, 255, 0.1)",
    color: theme.palette.type === "light" ? "#374151" : "#e2e8f0",
    transition: "all 0.3s ease",
    "&:hover": {
      background: theme.palette.type === "light"
        ? "rgba(255, 255, 255, 0.2)"
        : "rgba(255, 255, 255, 0.1)",
      transform: "scale(1.05)",
    },
  },

  // Campos de input melhorados mas compatíveis
  textField: {
    "& .MuiOutlinedInput-root": {
      borderRadius: "12px",
      backgroundColor: theme.palette.type === "light"
        ? "rgba(255, 255, 255, 0.8)"
        : "rgba(30, 30, 30, 0.8)",
      backdropFilter: "blur(10px)",
      transition: "all 0.3s ease",
      color: theme.palette.type === "light" ? "#000" : "#fff",
      "&:hover": {
        backgroundColor: theme.palette.type === "light"
          ? "rgba(255, 255, 255, 0.9)"
          : "rgba(30, 30, 30, 0.9)",
      },
      "&.Mui-focused": {
        backgroundColor: theme.palette.type === "light"
          ? "rgba(255, 255, 255, 1)"
          : "rgba(30, 30, 30, 1)",
        boxShadow: theme.palette.type === "light"
          ? "0 0 0 3px rgba(59, 130, 246, 0.1)"
          : "0 0 0 3px rgba(100, 150, 255, 0.2)",
      },
      "& fieldset": {
        borderColor: theme.palette.type === "light"
          ? "rgba(59, 130, 246, 0.2)"
          : "rgba(100, 150, 255, 0.3)",
      },
      "&:hover fieldset": {
        borderColor: theme.palette.type === "light"
          ? "rgba(59, 130, 246, 0.4)"
          : "rgba(100, 150, 255, 0.5)",
      },
      "&.Mui-focused fieldset": {
        borderColor: theme.palette.type === "light" ? "#3b82f6" : "#6496ff",
        borderWidth: "2px",
      },
    },
    "& .MuiInputLabel-root": {
      color: theme.palette.type === "light" ? "#6b7280" : "#94a3b8",
      fontWeight: 500,
      "&.Mui-focused": {
        color: theme.palette.type === "light" ? "#3b82f6" : "#6496ff",
      },
    },
  },

  // Seletor de idioma - versão simplificada
  languageSelector: {
    position: "fixed",
    top: "20px",
    left: "20px",
    zIndex: 1000,
    background: theme.palette.type === "light"
      ? "rgba(255, 255, 255, 0.1)"
      : "rgba(255, 255, 255, 0.05)",
    backdropFilter: "blur(10px)",
    borderRadius: "12px",
    border: theme.palette.type === "light"
      ? "1px solid rgba(255, 255, 255, 0.2)"
      : "1px solid rgba(255, 255, 255, 0.1)",
    padding: "8px 12px",
  },

  // Link de registro
  registerLink: {
    color: theme.palette.type === "light" ? "#3b82f6" : "#6496ff",
    textDecoration: "none",
    fontWeight: 600,
    transition: "all 0.3s ease",
    "&:hover": {
      color: theme.palette.type === "light" ? "#2563eb" : "#5078ff",
      textDecoration: "underline",
    },
  },

  // Estilos para o dropdown de idiomas
  languageDropdown: {
    display: "flex",
    alignItems: "center",
    background: "none",
    border: "none",
    color: theme.palette.type === "light" ? "white" : "#e2e8f0",
    fontSize: "14px",
    fontWeight: 500,
    cursor: "pointer",
    gap: "8px",
    transition: "opacity 0.3s ease",
    "&:hover": {
      opacity: 0.8,
    },
  },

  languageOptions: {
    position: "absolute",
    top: "100%",
    left: "0",
    marginTop: "8px",
    background: theme.palette.type === "light"
      ? "rgba(255, 255, 255, 0.95)"
      : "rgba(18, 18, 18, 0.95)",
    backdropFilter: "blur(20px)",
    boxShadow: theme.palette.type === "light"
      ? "0 10px 25px rgba(0, 0, 0, 0.15)"
      : "0 10px 25px rgba(0, 0, 0, 0.3)",
    borderRadius: "12px",
    border: theme.palette.type === "light"
      ? "1px solid rgba(255, 255, 255, 0.2)"
      : "1px solid rgba(255, 255, 255, 0.1)",
    padding: "8px",
    zIndex: 1000,
    minWidth: "140px",
  },

  languageOption: {
    background: "none",
    border: "none",
    color: theme.palette.type === "light" ? "#374151" : "#e2e8f0",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    width: "100%",
    padding: "8px 12px",
    textAlign: "left",
    borderRadius: "8px",
    fontSize: "14px",
    fontWeight: 500,
    cursor: "pointer",
    transition: "all 0.2s ease",
    "&:hover": {
      background: theme.palette.type === "light"
        ? "rgba(59, 130, 246, 0.1)"
        : "rgba(100, 150, 255, 0.1)",
      color: theme.palette.type === "light" ? "#3b82f6" : "#6496ff",
    },
  },


}));

const Login = () => {
  const classes = useStyles();
  const theme = useTheme();
  const { colorMode } = useContext(ColorModeContext);
  const { appLogoFavicon, appName } = colorMode;
  const mode = theme.palette.type;
  const [user, setUser] = useState({ email: "", password: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [allowSignup, setAllowSignup] = useState(false);
  const { getPublicSetting } = useSettings();
  const { handleLogin } = useContext(AuthContext);

  const [open, setOpen] = useState(false);
  const ref = useRef();
  const [enabledLanguages, setEnabledLanguages] = useState(["pt-BR", "en", "es"]);
  const [backgroundLight, setBackgroundLight] = useState("");
  const [backgroundDark, setBackgroundDark] = useState("");
  const [backgroundColorLight, setBackgroundColorLight] = useState("");
  const [backgroundColorDark, setBackgroundColorDark] = useState("");

  const getCompanyIdFromUrl = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const companyId = urlParams.get("companyId");
    return companyId ? parseInt(companyId) : null;
  };

  const handleChangeInput = (e) => {
    setUser({ ...user, [e.target.name]: e.target.value });
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handlSubmit = (e) => {
    e.preventDefault();
    handleLogin(user);
  };

  useEffect(() => {
    let isMounted = true;

    const loadSettings = async () => {
      const companyId = getCompanyIdFromUrl();

      try {
        // Paralelizar todas as chamadas de API
        const [
          userCreation,
          enabledLanguagesResult,
          appLogoBackgroundLight,
          appLogoBackgroundDark,
          backgroundColorLight,
          backgroundColorDark
        ] = await Promise.allSettled([
          getPublicSetting("userCreation", companyId),
          getPublicSetting("enabledLanguages", companyId),
          getPublicSetting("appLogoBackgroundLight", companyId),
          getPublicSetting("appLogoBackgroundDark", companyId),
          getPublicSetting("backgroundColorLight", companyId),
          getPublicSetting("backgroundColorDark", companyId)
        ]);

        if (!isMounted) return;

        // Processar userCreation
        if (userCreation.status === 'fulfilled') {
          setAllowSignup(userCreation.value === "enabled");
        }

        // Processar enabledLanguages
        let langs = ["pt-BR", "en", "es"];
        if (enabledLanguagesResult.status === 'fulfilled' && enabledLanguagesResult.value) {
          try {
            langs = JSON.parse(enabledLanguagesResult.value);
          } catch (e) {
            console.warn('Error parsing enabled languages:', e);
          }
        }
        setEnabledLanguages(langs);

        // Processar appLogoBackgroundLight
        if (appLogoBackgroundLight.status === 'fulfilled' && appLogoBackgroundLight.value) {
          setBackgroundLight(getBackendUrl() + "/public/" + appLogoBackgroundLight.value);
        } else {
          setBackgroundLight("");
        }

        // Processar appLogoBackgroundDark
        if (appLogoBackgroundDark.status === 'fulfilled' && appLogoBackgroundDark.value) {
          setBackgroundDark(getBackendUrl() + "/public/" + appLogoBackgroundDark.value);
        } else {
          setBackgroundDark("");
        }

        // Processar backgroundColorLight
        if (backgroundColorLight.status === 'fulfilled') {
          setBackgroundColorLight(backgroundColorLight.value || "");
        } else {
          setBackgroundColorLight("");
        }

        // Processar backgroundColorDark
        if (backgroundColorDark.status === 'fulfilled') {
          setBackgroundColorDark(backgroundColorDark.value || "");
        } else {
          setBackgroundColorDark("");
        }
      } catch (error) {
        console.error('Error loading public settings:', error);
      }
    };

    loadSettings();

    return () => {
      isMounted = false;
    };
  }, []);

  const current =
    languageOptions.find((opt) => opt.value === i18n.language) ||
    languageOptions[0];

  const handleSelect = (opt) => {
    i18n.changeLanguage(opt.value);
    localStorage.setItem("language", opt.value);
    setOpen(false);
    window.location.reload();
  };

  let finalBackground;
  if (mode === "light") {
    if (backgroundLight) {
      finalBackground = `url(${backgroundLight})`;
    } else if (backgroundColorLight) {
      finalBackground = backgroundColorLight;
    } else {
      finalBackground = "linear-gradient(135deg, #1e3a8a 0%, #3b82f6 50%, #60a5fa 100%)";
    }
  } else {
    if (backgroundDark) {
      finalBackground = `url(${backgroundDark})`;
    } else if (backgroundColorDark) {
      finalBackground = backgroundColorDark;
    } else {
      finalBackground = "linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #334155 100%)";
    }
  }

  finalBackground = String(finalBackground || "#f5f5f5");


  return (
    <>
      <Helmet>
        <title>{appName || "Sistema"}</title>
        <link rel="icon" href={appLogoFavicon || "/favicon.ico"} />
      </Helmet>
      
      <div className={clsx(classes.root, "login-page")}
      style={{   
          width: "100vw",
          height: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "0",
          margin: "0",
          boxSizing: "border-box",
          overflow: "hidden",
          backgroundColor:
            typeof finalBackground === "string" &&
            finalBackground.includes("url(")
              ? "transparent"
              : finalBackground,
          backgroundImage:
            typeof finalBackground === "string" &&
            finalBackground.includes("url(")
              ? finalBackground
              : "none",
          backgroundRepeat: "no-repeat",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        {/* Seletor de idioma */}
        <div
          ref={ref}
          className={classes.languageSelector}
        >
          <button 
            onClick={() => setOpen((o) => !o)}
            className={classes.languageDropdown}
          >
            <span style={{ fontSize: '18px', marginRight: '8px' }}>
              {current.emoji}
            </span>
            {current.label}
            <span>▾</span>
          </button>

          {open && (
            <div className={classes.languageOptions}>
              {languageOptions
                .filter((opt) => enabledLanguages.includes(opt.value))
                .map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => handleSelect(opt)}
                    className={classes.languageOption}
                  >
                    <span style={{ fontSize: '16px', marginRight: '8px' }}>
                      {opt.emoji}
                    </span>
                    {opt.label}
                  </button>
                ))}
            </div>
          )}
        </div>

        <Container
          component="main"
          maxWidth="xs"
          className={classes.containerLogin}
        >
          <CssBaseline />
          <div className={classes.paper}>
            <IconButton
              className={classes.iconButton}
              onClick={colorMode.toggleColorMode}
            >
              {mode === "dark" ? <Brightness7Icon /> : <Brightness4Icon />}
            </IconButton>
            
            <div>
              <img className={classes.logoImg} alt="logo" />
            </div>
            
            <form className={classes.form} noValidate onSubmit={handlSubmit}>
              <TextField
                variant="outlined"
                margin="normal"
                required
                fullWidth
                id="email"
                label={i18n.t("login.form.email")}
                name="email"
                value={user.email}
                onChange={handleChangeInput}
                autoComplete="email"
                autoFocus
               
              />
              <TextField
                variant="outlined"
                margin="normal"
                required
                fullWidth
                name="password"
                label={i18n.t("login.form.password")}
                type={showPassword ? "text" : "password"}
                id="password"
                value={user.password}
                onChange={handleChangeInput}
                autoComplete="current-password"
               
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={togglePasswordVisibility}
                        edge="end"
                        style={{ color: "#6b7280" }}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                color="primary"
                className={classes.submit}
              >
                {i18n.t("login.buttons.submit")}
              </Button>
              {allowSignup && (
                <Grid container justifyContent="center">
                  <Grid item>
                    <Link
                      href="#"
                      variant="body2"
                      component={RouterLink}
                      to="/signup"
                      className={classes.registerLink}
                    >
                      {i18n.t("login.buttons.register")}
                    </Link>
                  </Grid>
                </Grid>
              )}
            </form>
          </div>
        </Container>
      </div>
    </>
  );
};

export default Login;